import { useEffect, useCallback, useRef } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import { useService } from '../../../../contexts/ServiceContext'

const severityColors = {
  FATAL: 'error',
  ERROR: 'error',
  WARN: 'warning',
  INFO: 'info',
  DEBUG: 'default',
  UNKNOWN: 'default',
}

function ServiceLogsPanel({ active }) {
  const { logs, logsLoading, loadLogs } = useService()
  const loadLogsRef = useRef(loadLogs)
  loadLogsRef.current = loadLogs

  useEffect(() => {
    if (!active) return

    loadLogsRef.current()
    const interval = setInterval(() => {
      loadLogsRef.current()
    }, 10000)
    return () => clearInterval(interval)
  }, [active])

  const formatTimestamp = useCallback((timestamp) => {
    return (
      <FormattedDate
        value={timestamp}
        year="numeric"
        month="short"
        day="numeric"
        hour="numeric"
        minute="numeric"
        second="numeric"
      />
    )
  }, [])

  if (logsLoading && logs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        <FormattedMessage id="dockerServices.logs.empty" />
      </Typography>
    )
  }

  return (
    <List disablePadding>
      {logs.map((log) => (
        <ListItem
          key={log.id}
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            borderBottom: 1,
            borderColor: 'divider',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
            <Chip
              label={log.severity}
              size="small"
              color={severityColors[log.severity] || 'default'}
              sx={{ minWidth: 60 }}
            />
            <Chip
              label={log.stream}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(log.loggedAt)}
            </Typography>
          </Box>
          <ListItemText
            primary={log.message}
            primaryTypographyProps={{
              variant: 'body2',
              sx: {
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  )
}

export default ServiceLogsPanel
